import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { gselectedgroup, gloggedinuser, campaignidnamepair } from "../../interfaces";
import { MdCall } from "react-icons/md";
import { MdCallEnd } from "react-icons/md";
import { IconButton } from "@fluentui/react/lib/Button";
import { Dropdown } from "@fluentui/react";
import styles from "./TwiLMWebCCA.module.css";
import { genNewConvoKey } from "./../../util_glob";
import { set_local_cache_selected_campaign_id_oer_space, getActiveCampaignId, get_campaigns_for_selected_group } from "../../pages/chat/ChatUtil";
import { campaignInputVoiceDropdownStyles } from "../../styles_glob";
import { Device } from '@twilio/voice-sdk';
import { qGetUP } from "../../api/qpi";


interface TwilioCallProps {
    selectedGroup: gselectedgroup;
    loggedInUser: gloggedinuser; // new prop for identifying the user
    activeConvoKey: string;
    histGetByKey: (userid: string, key: string, initial_key: string) => void;
    newConversation: (convoKey?: string) => void; // New prop for creating a new conversation
}


const TwiLMWebCCA: React.FC<TwilioCallProps>    = ({ selectedGroup, loggedInUser, activeConvoKey, histGetByKey, newConversation }) => {
    const [status, setStatus]                   = useState("Click 'Call' to connect.");
    const [callDisabled, setCallDisabled]       = useState(false);
    const [endDisabled, setEndDisabled]         = useState(true);
    const [isInitializing, setIsInitializing]   = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>(() => getActiveCampaignId(selectedGroup, get_campaigns_for_selected_group(selectedGroup)));

    // derive active campaigns from selectedGroup.camp_join
    const campaigns = useMemo<campaignidnamepair[]>(() => {
        return get_campaigns_for_selected_group(selectedGroup);
    }, [selectedGroup.campaigns_enabled, selectedGroup.camp_join]);

    // restore selected campaign from local storage or default
    useEffect(() => {
            setSelectedCampaignId(getActiveCampaignId(selectedGroup, campaigns));
            setStatus('')
    }, [campaigns, selectedGroup.selectionId, selectedGroup.def_campaign_id]);

    const deviceRef         = useRef<Device | null>(null);
    const tokenExpiry       = useRef<number | null>(null);
    const twilioReady       = useRef(false);
    const isInitializingRef = useRef(isInitializing);
    useEffect(() => {
        isInitializingRef.current = isInitializing;
    }, [isInitializing]);

    useEffect(() => {
        let intervalId: any = null; // to store the interval id
        // check if the condition to run the interval is met
        if (endDisabled === false) {
            // start the interval
            intervalId = setInterval(() => {
                histGetByKey(loggedInUser.userId, activeConvoKey, activeConvoKey);
            }, 2000); // calls every 2 seconds
        } else {
            import.meta.env.DEV === true && console.log("‹interval_not_started›‾‹endDisabled_true›");
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [endDisabled, loggedInUser, activeConvoKey]); // dependencies for the effect

    const twi_acc = selectedGroup.line_config.account_sid;
    let twi_lmapp = selectedGroup.line_config.twi_lmapp;

    import.meta.env.DEV === true && (twi_lmapp = "AP90d19dad5bccbe90447a6b1758d48b68");

    const fetchToken = useCallback(async (): Promise<string | null> => {
        try {
            const res = await fetch(`/kgnf_twi_to?twi_acc=${twi_acc}&twi_lmapp=${twi_lmapp}`);
            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`Failed to fetch token: ${res.status} ${res.statusText}. Details: ${errorText}`);
            }
            const data = await res.json();
            if (!data.token) throw new Error("Token not found in response");

            const payload = JSON.parse(atob(data.token.split(".")[1]));
            const exp = payload.exp * 1000;
            tokenExpiry.current = exp;
            const now = Date.now();
            if (exp - now <= 0) {
                console.warn("‹already‾expired›");
                return null;
            }
            return data.token;
        } catch (err: any) {
            console.error("Fetch token error:", err);
            setStatus(`Error fetching token: ${err.message}`);
            tokenExpiry.current = null;
            return null;
        }
    }, [twi_acc, twi_lmapp]);

    const initTwilioDevice = useCallback(async (): Promise<void> => {
        const token = await fetchToken();
        if (!token) throw new Error('Failed to initialize: Could not get a valid token.');

        // construct device; signaling opens later on register()/connect()
        const device = new Device(token, { logLevel: 'warn', edge: 'roaming' });
        deviceRef.current = device;

        // v2 events
        device.on('registered', () => {
            setStatus('Device Ready. You can make a call.');
            setCallDisabled(false);
            setEndDisabled(true);
            twilioReady.current = true;
        });

        device.on('error', (error) => {
            setStatus(`Error: ${error.message}`);
            twilioReady.current = false;
            setCallDisabled(true);
            setEndDisabled(true);
        });

        device.on('incoming', (call) => {
            setStatus(`Incoming call from ${call.parameters?.From ?? ''}`);
        });

        device.on('tokenWillExpire', async () => {
            // native token refresh flow in v2
            const newToken = await fetchToken();
            if (newToken) {
                device.updateToken(newToken);
            } else {
                setStatus("Failed to refresh token. Calls may fail.");
                twilioReady.current = false;
                setCallDisabled(true);
            }
        });

        // open signaling + register for incoming calls
        await device.register();
    }, [fetchToken]);

    const handleCall = async () => {

        let target_up = await qGetUP(loggedInUser, selectedCampaignId);

        if (isInitializing) {
            console.log("Initialization already in progress.");
            return;
        }
        const convoKey = genNewConvoKey(selectedGroup.assistanttype);
        const params = {
            ClientIdentifier: convoKey,
            SpaceIdentifier: selectedGroup.selectionId,
            UserIdentifier: loggedInUser.userId,
            CampaignId: selectedCampaignId,
            TargetId: target_up?.tarid || '',
            TargetFrom: target_up?.tarph || '',
            TargetName: target_up?.tarname || '',
        };
        if (!twilioReady.current) {
            setIsInitializing(true);
            setCallDisabled(true);
            setStatus('Initializing Twilio & Connecting...');
            console.log("Twilio not ready. Starting initialization...");
            try {
                await initTwilioDevice();
            } catch (error: any) {
                setStatus(`Failed, try refreshing: ${error.message}`);
                console.error("handleCall: Initialization failed:", error);
                setCallDisabled(false);
                twilioReady.current = false;
                return;
            } finally {
                setIsInitializing(false);
            }
        }

        if (!deviceRef.current) {
            setStatus("Device not available. Please try again.");
            setCallDisabled(false);
            return;
        }

        newConversation(convoKey);
        setStatus('Connecting call...');
        setCallDisabled(true);

        try {
            // v2 connect
            const call = await deviceRef.current.connect({ params });

            call.on('accept', () => {
                setStatus('Call Connected.');
                setCallDisabled(true);
                setEndDisabled(false);
                console.log("Twilio Call Connected", call);
            });

            call.on('disconnect', () => {
                setStatus('Call Disconnected.');
                setCallDisabled(false);
                setEndDisabled(true);
                if (deviceRef.current && twilioReady.current) twilioReady.current = true;

                console.log("Twilio Call Disconnected");
            });
        } catch (error: any) {
            console.error("Call connection failed:", error);
            setStatus(`Call failed: ${error.message}`);
            setCallDisabled(false);
            setEndDisabled(true);
        }
    };

    const handleEnd = () => {
        if (deviceRef.current) {
            console.log("Ending call via disconnectAll().");
            setStatus("Ending call...");
            deviceRef.current.disconnectAll();
        }
    };

    useEffect(() => {
        return () => {
            console.log("TwiLMWebCCA: Unmounting. Cleaning up Twilio Device and intervals.");
            deviceRef.current?.destroy();
            deviceRef.current = null;
            twilioReady.current = false;
        };
    }, []);

    return (
        <div className={styles.container}>
            {/* campaign selector */}
            {campaigns.length > 1 && (
                <div className={styles.campaignSelect}>
                    <Dropdown
                        placeholder="Campaign"
                        options={campaigns.map(c => ({ key: c.id, text: c.name }))}
                        selectedKey={selectedCampaignId || undefined}
                        onChange={(_, opt) => {
                            const id = (opt?.key as string) ?? '';
                            setSelectedCampaignId(id);
                            set_local_cache_selected_campaign_id_oer_space(selectedGroup.selectionId, id);
                        }}
                        styles={campaignInputVoiceDropdownStyles}
                    />
                </div>
            )}
            <div className={styles.status} title={status}>
                {status}
            </div>
            <IconButton
                id="twiLMWebCCACallBtn"
                onClick={handleCall}
                disabled={callDisabled || isInitializing}
                styles={{
                    root: {
                        width: "100%",
                        height: 30,
                        fontSize: 16,
                        fontFamily: 'Urbanist, Roboto, sans-serif',
                        fontWeight: 600,
                        marginTop: "5px",
                        backgroundColor: "#38a169",
                        color: "white",
                        border: "none",
                        borderRadius: 4,
                        marginBottom: 5,
                        cursor: callDisabled || isInitializing ? "not-allowed" : "pointer",
                        opacity: callDisabled || isInitializing ? 0.6 : 1
                    },
                    rootHovered: { backgroundColor: "#2f855a", color: "white", border: "none" },
                    rootPressed: { backgroundColor: "#276749", color: "white", border: "none" },
                    label: { overflow: "visible", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                }}
            >
                <MdCall color="#E4572E" size={22} />
                <div className={styles.callEndLabel}>Call</div>
            </IconButton>
            <IconButton
                onClick={handleEnd}
                disabled={endDisabled}
                styles={{
                    root: {
                        width: "100%",
                        fontFamily: 'Urbanist, Roboto, sans-serif',
                        height: 30,
                        fontSize: 16,
                        fontWeight: 600,
                        marginRight: 30,
                        backgroundColor: "#e53e3e",
                        color: "white",
                        marginTop: "0px",
                        borderRadius: 4,
                        cursor: endDisabled ? "not-allowed" : "pointer",
                        opacity: endDisabled ? 0.6 : 1
                    },
                    rootHovered: { backgroundColor: "#c53030", color: "white", border: "none" },
                    rootPressed: { backgroundColor: "#a02727", color: "white", border: "none" },
                    label: { overflowY: "visible", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                    rootDisabled: { backgroundColor: "#c5c5c5", color: "white", border: "none", opacity: 0.6 }
                }}
            >
                <MdCallEnd color="#ff2f38" size={24} />
                <div className={styles.callEndLabel}>End</div>
            </IconButton>
        </div>
    );
};

export default TwiLMWebCCA;
